import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Fetches a count per status key in parallel (e.g. via `list(..., { size: 1, status })`
 * and reading `totalElements`), for driving a clickable status-filter bar with counts.
 * Refetches whenever `deps` change or `refetch()` is called explicitly (e.g. after a
 * create/update/delete that could move a record between statuses).
 */
export function useStatusCounts<S extends string>(
  keys: readonly S[],
  fetchCount: (key: S) => Promise<number>,
  deps: unknown[] = [],
): { counts: Partial<Record<S, number>>; loading: boolean; refetch: () => void } {
  const [counts, setCounts] = useState<Partial<Record<S, number>>>({});
  const [loading, setLoading] = useState(true);
  const fetchCountRef = useRef(fetchCount);
  fetchCountRef.current = fetchCount;

  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(keys.map(key => fetchCountRef.current(key).then(n => [key, n] as const)))
      .then(entries => {
        if (cancelled) return;
        setCounts(Object.fromEntries(entries) as Partial<Record<S, number>>);
      })
      .catch(() => {
        if (cancelled) return;
        setCounts({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { counts, loading, refetch };
}
